import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useEffect, useContext, useState } from 'react'
import { Breadcrumbs, Typography } from '@mui/material'

import ChartIcon from '../assets/icons/chart-icon.svg'
import FundingsIcon from '../assets/icons/fundings-icon.svg'
import '../components/new-insights-management/styles/index.css'
import { AppStateContext, AuthContext, UserContext } from '../contexts'
import { Filters } from '../components/new-insights-management/filters'
import { TopStats } from '../components/new-insights-management/top-stats'
import { BalanceCard } from '../components/new-insights-management/balance-card'
import { PaymentBatches } from '../components/new-insights-management/payment-batches'
import { PaymentTimeline } from '../components/new-insights-management/payment-timeline'
import { PaymentStatusChart } from '../components/new-insights-management/payment-status-chart'
import { PaymentDistribution } from '../components/new-insights-management/payment-distribution'
import { UserSessionType } from '../types'
import { ReportSquareContainer } from '../components/reports-brief-management/report-square-container'
import { numberWithCommas } from '../utils'
import { requestAccessToken } from '../api/auth.api'
import { getInsightsBriefData } from '../api/app-queries.api'
import { countriesCurrencies } from '../lib'

export type PaymentsWithNoveltiesState = { paymentsWithNoveltiesPending: number; paymentsWithNoveltiesApproved: number; paymentsWithNoveltiesRejected: number }

export enum Filter {
  LAST_TWO_HOURS,
  LAST_DAY,
  LAST_WEEK,
  LAST_MONTH,
  CUSTOM
}

// const SERVER_AHEAD_HOURS = 5
const HOUR_MS = 60 * 60 * 1000
const DAY_MS = 24 * HOUR_MS

const NewInsightsManagement = () => {
  const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
  const selectedCurrency = (countriesCurrencies as { [key: string]: string })[selectedCountry].toLowerCase()

  const navigate = useNavigate()
  const { isAuth } = useContext(AuthContext)
  const { userSession } = useContext(UserContext)
  const { updateLoadingStatus, defaultPocketIdSelected } = useContext(AppStateContext)
  const { t } = useTranslation(['customer-company-details-management', 'reports-management'])

  const [tradedVolume, setTradedVolume] = useState<number>(0)
  const [numOfBatchesSent, setNumOfBatchesSent] = useState<number>(0)
  const [dataFilter, setDataFilter] = useState<Filter>(Filter.LAST_DAY)
  const [customerCompanyId, setCustomerCompanyId] = useState<string>('')
  const [paymentsPaidQuantity, setPaymentsPaidQuantity] = useState<number>(0)
  const [numOfBatchesForReview, setNumOfBatchesForReview] = useState<number>(0)
  const [queriesOfRestrictive, setQueriesOfRestrictive] = useState<number>(0)
  const [paymentsPendingQuantity, setPaymentsPendingQuantity] = useState<number>(0)
  const [noveltiesSolvedQuantity, setNoveltiesSolvedQuantity] = useState<number>(0)
  const [numOfBatchesToBeDisperse, setNumOfBatchesToBeDisperse] = useState<number>(0)
  const [paymentsReturnedQuantity, setPaymentsReturnedQuantity] = useState<number>(0)
  const [filterDates, setFilterDates] = useState<{ $gte: any; $lte: any } | null>(null)
  const [paymentsValidatedQuantity, setPaymentsValidatedQuantity] = useState<number>(0)
  const [paymentsErrorOrRejectedQuantity, setPaymentsErrorOrRejectedQuantity] = useState<number>(0)
  const [customerCompanyFundingsQuantity, setCustomerCompanyFundingsQuantity] = useState<number>(0)
  const [paymentsDataByHour, setPaymentsDataByHour] = useState<Array<{ count: number; hour: string }>>([])
  const [paymentsWithBackgroundResearchQuantity, setPaymentsWithBackgroundResearchQuantity] = useState<number>(0)
  const [paymentsWithNoveltiesState, setPaymentsWithNoveltiesState] = useState<PaymentsWithNoveltiesState | null>(null)

  useEffect(() => {
    const originalWarn = console.warn
    setTimeout(() => updateLoadingStatus?.(true), 1)

    console.warn = (message, ...args) => {
      if (typeof message === 'string' && message.includes('The width') && message.includes('are both fixed numbers')) {
        return
      }
      originalWarn(message, ...args)
    }

    return () => {
      console.warn = originalWarn
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!userSession) {
      return
    }

    if (userSession.role !== 'super-admin-client' && userSession.role !== 'admin-client') {
      navigate('/404')
      return
    }

    setCustomerCompanyId(userSession.belongsTo)
    handleSetInsightsData(Filter.LAST_DAY)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession, dataFilter])

  useEffect(() => {
    if (filterDates) {
      handleGetDataByGivenFilter(filterDates)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterDates])

  const handleGetDataByGivenFilter = async (filterApplied: { $gte: any; $lte: any }) => {
    try {
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const { data } = await getInsightsBriefData({ selectedCountry, filterApplied, defaultPocketIdSelected }, accessToken)

      console.log('data', data)
      handleSetSectionData(data)
    } catch (error) {
      console.log('Error handleGetDataByGivenFilter > ', error)
    }
  }

  const handleSetSectionData = (data: any) => {
    const numPaymentsPaid = data.paymentsStatsData.byStatus.length === 0 ? 0 : Number(data.paymentsStatsData.byStatus.find((byStatusData: any) => byStatusData.status === 'paid')?.count)
    const numPaymentsPending = data.paymentsStatsData.byStatus.length === 0 ? 0 : Number(data.paymentsStatsData.byStatus.find((byStatusData: any) => byStatusData.status === 'other')?.count)
    const numPaymentsReturned = data.paymentsStatsData.byStatus.length === 0 ? 0 : Number(data.paymentsStatsData.byStatus.find((byStatusData: any) => byStatusData.status === 'returned')?.count)
    const numPaymentsValidated = data.paymentsStatsData.byStatus.length === 0 ? 0 : Number(data.paymentsStatsData.byStatus.find((byStatusData: any) => byStatusData.status === 'validated')?.count)
    const numPaymentsRejected = data.paymentsStatsData.byStatus.length === 0 ? 0 : Number(data.paymentsStatsData.byStatus.find((byStatusData: any) => byStatusData.status === 'rejected')?.count)

    setPaymentsPaidQuantity(numPaymentsPaid)
    setPaymentsPendingQuantity(numPaymentsPending)
    setPaymentsReturnedQuantity(numPaymentsReturned)
    setPaymentsValidatedQuantity(numPaymentsValidated)
    setPaymentsErrorOrRejectedQuantity(numPaymentsRejected)
    setPaymentsDataByHour(data.paymentsStatsData.byHour)
    setNumOfBatchesSent(Number(data.batchesStatsData.numTotalOfBatches))
    setNumOfBatchesForReview(Number(data.batchesStatsData.numBatchesForReview))
    setQueriesOfRestrictive(Number(data.paymentsStatsData.totals.totalPayments))
    setNumOfBatchesToBeDisperse(Number(data.batchesStatsData.numBatchesReady))
    setCustomerCompanyFundingsQuantity(Number(data.fundingsStatsData.numTotalOfFundings))
    setNoveltiesSolvedQuantity(Number(data.paymentsStatsData.totals.paymentsWithResolvedNovelties))
    setPaymentsWithBackgroundResearchQuantity(Number(data.paymentsStatsData.totals.paymentsWithPendingBackgroundResearch))
    setPaymentsWithNoveltiesState({
      paymentsWithNoveltiesPending: Number(data.paymentsStatsData.totals.paymentsWithPendingNovelties),
      paymentsWithNoveltiesApproved: Number(data.paymentsStatsData.totals.paymentsWithApprovedNovelties),
      paymentsWithNoveltiesRejected: Number(data.paymentsStatsData.totals.paymentsWithRejectedNovelties)
    })
    setTradedVolume(data.paymentsStatsData.totals.totalAmount.$numberDecimal ? Number(data.paymentsStatsData.totals.totalAmount.$numberDecimal) : Number(data.paymentsStatsData.totals.totalAmount))

    updateLoadingStatus?.(false)
  }

  const handleSetInsightsData = (filterApplied: Filter, fromDateToFilter?: Date, toDateToFilter?: Date) => {
    // const offsetMs = SERVER_AHEAD_HOURS * HOUR_MS --> Esto lo ponemos si es necesario
    const to = new Date(Date.now())
    let filterDataToBeApplied = {} as { $gte: Date; $lte?: Date }
    filterDataToBeApplied.$lte = to

    if (filterApplied === Filter.LAST_DAY) {
      const twentyFourHoursAgo = new Date(to.getTime() - 24 * HOUR_MS)
      filterDataToBeApplied.$gte = twentyFourHoursAgo
    } else if (filterApplied === Filter.LAST_TWO_HOURS) {
      const twoHoursAgo = new Date(to.getTime() - 2 * HOUR_MS)
      filterDataToBeApplied.$gte = twoHoursAgo
    } else if (filterApplied === Filter.LAST_WEEK) {
      const oneWeekAgo = new Date(to.getTime() - 7 * DAY_MS)
      filterDataToBeApplied.$gte = oneWeekAgo
    } else if (filterApplied === Filter.LAST_MONTH) {
      const oneMonthAgo = new Date(to.getTime() - 30 * DAY_MS)
      filterDataToBeApplied.$gte = oneMonthAgo
    } else {
      filterDataToBeApplied.$gte = new Date((fromDateToFilter as Date).getTime())
      filterDataToBeApplied.$lte = new Date((toDateToFilter as Date).getTime())
    }

    setFilterDates(filterDataToBeApplied as { $gte: any; $lte: any })
  }

  return (
    <div className="section-container">
      <Breadcrumbs aria-label="breadcrumb">
        <Typography
          key="3"
          className="breadcrumb-active"
        >
          {t('breadcrumbs.insights')}
        </Typography>
      </Breadcrumbs>
      <div style={{ marginTop: '24px' }}>
        <BalanceCard />
        <Filters
          dataFilter={dataFilter}
          setDataFilter={setDataFilter}
          handleApplyFilterToData={(filterApplied: Filter) => {
            updateLoadingStatus?.(true)
            handleSetInsightsData(filterApplied)
            // handleSyncDocumentsDataFromRemoteDB(filterApplied)
          }}
          handleApplyCustomFilterToData={(fromDateToFilter: Date, toDateToFilter: Date) => {
            updateLoadingStatus?.(true)
            handleSetInsightsData(Filter.CUSTOM, fromDateToFilter, new Date(toDateToFilter.setHours(23, 59, 59, 999)))
            // handleSyncDocumentsDataFromRemoteDB(Filter.CUSTOM, fromDateToFilter, new Date(toDateToFilter.setHours(23, 59, 59, 999)))
          }}
        />
        <TopStats
          userSession={userSession as UserSessionType}
          customerCompanyId={customerCompanyId}
          queriesOfRestrictive={queriesOfRestrictive}
          noveltiesSolvedQuantity={noveltiesSolvedQuantity}
          paymentsWithBackgroundResearchQuantity={paymentsWithBackgroundResearchQuantity}
        />
        <div className="reports-row-container customer-company-reports-row-container">
          {(userSession?.role.includes('super-admin') || (userSession?.role.includes('admin-client') && String(userSession?.belongsTo) === '67237f891071ccf4fd8ebc28')) && (
            <ReportSquareContainer
              showMainIcon
              icon={FundingsIcon}
              showSlimNavigationButton
              showSecondaryData={false}
              mainData={numberWithCommas(Number(customerCompanyFundingsQuantity))}
              reportName={t('reportsNames.fundingsMade', { ns: 'reports-management' })}
              handleButtonGoDetailsAction={() => navigate(`/customer-company-fundings-history/${customerCompanyId}`, { state: { previousPath: 'insights' } })}
            />
          )}
          <ReportSquareContainer
            showMainIcon
            icon={ChartIcon}
            showSecondaryData={false}
            showSlimNavigationButton={false}
            mainData={`$ ${numberWithCommas(Number(tradedVolume).toFixed(2))} ${selectedCurrency.toUpperCase()}`}
            reportName={t('reportsNames.tradedVolume', { ns: 'reports-management' })}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 w-full mt-6 custom-grid-cols-1">
          <div className="w-full">
            <PaymentDistribution
              key="distribution"
              customerCompanyId={customerCompanyId}
              paymentsPaidQuantity={paymentsPaidQuantity}
              paymentsPendingQuantity={paymentsPendingQuantity}
              paymentsReturnedQuantity={paymentsReturnedQuantity}
              paymentsValidatedQuantity={paymentsValidatedQuantity}
              paymentsErrorOrRejectedQuantity={paymentsErrorOrRejectedQuantity}
            />
          </div>
          <div className="w-full">
            <PaymentTimeline
              key="timeline"
              paymentsDataByHour={paymentsDataByHour}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 w-full mt-6 md:mt-8 custom-grid-cols-1">
          <div className="w-full">
            <PaymentBatches
              key="batches"
              numOfBatchesSent={numOfBatchesSent}
              numOfBatchesForReview={numOfBatchesForReview}
              numOfBatchesToBeDisperse={numOfBatchesToBeDisperse}
            />
          </div>
          <div className="w-full">
            <PaymentStatusChart
              key="status"
              paymentsWithNoveltiesState={paymentsWithNoveltiesState}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default NewInsightsManagement
